import { createApp } from 'vue';
import { createPinia } from 'pinia';
import App from './App.vue';

const app = createApp(App);
const pinia = createPinia();

app.use(pinia);
app.mount('#app');

// PWA Service Worker Registration
if ('serviceWorker' in navigator && import.meta.env.PROD) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js', {
      scope: '/'
    })
    .then((registration) => {
      console.log('🚀 PWA: Service Worker registered successfully', registration.scope);
      
      // Check for updates
      registration.addEventListener('updatefound', () => {
        const newWorker = registration.installing;
        
        newWorker.addEventListener('statechange', () => {
          if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
            // New content available
            showUpdateNotification();
          }
        });
      });
    })
    .catch((error) => {
      console.error('❌ PWA: Service Worker registration failed', error);
    });
  });
}

// PWA Install Prompt
let deferredPrompt;

window.addEventListener('beforeinstallprompt', (e) => {
  console.log('🚀 PWA: Install prompt available');
  e.preventDefault();
  deferredPrompt = e;
  renderInstallCTA();
});

window.addEventListener('appinstalled', () => {
  console.log('🎉 PWA: App installed successfully');
  removeInstallCTA();
  deferredPrompt = null;
});

// 🆕 Proactive install CTA check for browsers that don't fire beforeinstallprompt
window.addEventListener('load', () => {
  // Wait a bit for beforeinstallprompt to potentially fire
  setTimeout(() => {
    // If no prompt was triggered, check if we should show CTA anyway
    if (!deferredPrompt && !document.querySelector('.pwa-install-cta')) {
      const ua = navigator.userAgent || navigator.vendor || '';
      const isSafari = /Safari\//.test(ua) && !/Chrome\//.test(ua);
      const isFirefox = /Firefox\//.test(ua);
      
      // Show CTA for Safari and Firefox even without native prompt
      if (isSafari || isFirefox) {
        console.log('🚀 PWA: Showing proactive install CTA for', isSafari ? 'Safari' : 'Firefox');
        renderInstallCTA();
      }
    }
  }, 1000);
});

// Show update notification
function showUpdateNotification() {
  if (confirm('🚀 New version available! Reload to update?')) {
    window.location.reload();
  }
}

// Cross-browser Install CTA
function renderInstallCTA() {
  // Only on secure origins or localhost
  const isSecure = location.protocol === 'https:' || location.hostname === 'localhost';
  if (!isSecure) return;

  // Avoid dupes
  if (document.querySelector('.pwa-install-cta')) return;

  const ua = navigator.userAgent || navigator.vendor || '';
  const isSafari = /Safari\//.test(ua) && !/Chrome\//.test(ua);
  const isIOS = /iPhone|iPad|iPod/.test(ua);
  const isMac = /Mac OS X/.test(ua);
  const isFirefox = /Firefox\//.test(ua);
  const isAndroid = /Android/.test(ua);

  const cta = document.createElement('div');
  cta.className = 'pwa-install-cta';
  cta.style.cssText = `
    position: fixed; bottom: 20px; right: 20px; z-index: 1000;
    display: flex; gap: 8px; align-items: center;
  `;

  const button = document.createElement('button');
  button.className = 'pwa-install-button';
  button.style.cssText = `
    background: linear-gradient(135deg, #4CAF50, #45a049);
    color: white; border: none; padding: 12px 16px;
    border-radius: 20px; cursor: pointer; font-weight: 600;
    box-shadow: 0 4px 12px rgba(76, 175, 80, 0.3);
  `;

  if (deferredPrompt) {
    // Chrome-like flow
    button.textContent = '📱 Install App';
    button.onclick = async () => {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      console.log(`🚀 PWA: Install prompt outcome: ${outcome}`);
      deferredPrompt = null;
      removeInstallCTA();
    };
    cta.appendChild(button);
  } else if (isSafari) {
    // Safari instructions - more specific
    button.textContent = '📱 Add to Home Screen';
    button.onclick = () => {
      const instruction = isIOS 
        ? 'On iPhone/iPad:\n\n1. Tap the Share button (📤) at the bottom\n2. Scroll down and tap "Add to Home Screen"\n3. Tap "Add" to confirm'
        : isMac
        ? 'On Mac Safari:\n\n1. Click "File" in the menu bar\n2. Select "Add to Dock..."\n\nOr:\n\n1. Click the Share button in the address bar\n2. Select "Add to Dock"'
        : 'Use the Share button and look for "Add to Home Screen" or "Add to Dock" option.';
      
      alert(instruction);
    };
    cta.appendChild(button);
  } else if (isFirefox) {
    // Firefox guidance - platform specific
    button.textContent = isAndroid ? '📱 Install App' : '📱 Add to Desktop';
    button.onclick = () => {
      const instruction = isAndroid
        ? 'On Firefox for Android:\n\n1. Tap the menu (⋮) button\n2. Look for "Install" or "Add to Home screen"\n3. Tap to install the app'
        : 'On Firefox Desktop:\n\nInstallation as an app is limited on desktop Firefox.\n\nYou can:\n• Bookmark this page for quick access\n• Create a desktop shortcut\n• Use Firefox on Android for full app installation';
      
      alert(instruction);
    };
    cta.appendChild(button);
  } else {
    // Hide if not supported
    return;
  }

  document.body.appendChild(cta);
}

function removeInstallCTA() {
  document.querySelector('.pwa-install-cta')?.remove();
}

